import angular from 'angular';
import { compact } from 'lodash-es';

import { mapBezier } from './google-map.math';
import { fromDot, toDot, getDynamicMapStyle } from './google-map.style';

const geocode = (street, city, state, zip) => {
  return new Promise((resolve, reject) => {
    // Checks that the address returned from Google has the same state as provided
    const sameState = (components = []) => {
      return (
        Array.from(components).findIndex(
          component => component.short_name === state
        ) > -1
      );
    };

    // Helper function that requests geocode from Google
    // If the geocoded location doesn't exist or doesn't match,
    // then it retries with only the state
    const _geocode = (req, retry = true) => {
      new google.maps.Geocoder().geocode(req, (result, status) => {
        if (
          status === 'OK' &&
          sameState(result[0] && result[0].address_components)
        ) {
          return resolve(result[0].geometry.location);
        }

        // Reject since we've already retried
        if (!retry) {
          return reject();
        }

        // Retry since the status was either "ZERO_RESULTS" or the original state did not
        // match the returned state address component
        if (status === 'ZERO_RESULTS' || status === 'OK') {
          return _geocode({ address: `${state}, USA` }, false);
        }

        // Reject since we're done and didn't find anything
        return reject();
      });
    };

    _geocode({
      address: compact([street, city, state, zip, 'USA']).join(','),
    });
  });
};

const addInfoBox = (map, marker, city, state) => {
  const box = document.createElement('div');
  box.style.cssText = `font-family: Maquette;
     position: relative;
     color:#fff;
     background-color: #404041;
     padding: 10px;
     text-align: center;
     width: 200px;
     line-height: 1;
     letter-spacing: 2px;
     border-radius: 3px;
     width: 200px;
    `;

  const cityState = document.createElement('div');
  cityState.innerHTML = city + ', ' + state;
  box.appendChild(cityState);

  const arrow = document.createElement('div');
  arrow.style.cssText = `position: absolute;
     bottom:-7px;
     left:105px;
     width:0px;
     height:0px;
     border-left: 5px solid transparent;
     border-right: 5px solid transparent;
     border-top: 7px solid #404041;
    `;
  box.appendChild(arrow);

  const pixelOffsetX = -109;
  const pixelOffsetY = -55;

  const infoBox = new InfoBox({
    content: box,
    disableAutoPan: true,
    maxWidth: 0,
    pixelOffset: new google.maps.Size(pixelOffsetX, pixelOffsetY),
    zIndex: null,
    boxStyle: {},
    infoBoxClearance: new google.maps.Size(1, 1),
    isHidden: false,
    pane: 'floatPane',
    closeBoxURL: '',
    enableEventPropagation: false,
  });

  google.maps.event.addListener(marker, 'click', function(e) {
    return infoBox.open(map, this);
  });

  return infoBox.open(map, marker);
};

const addMarker = (
  map,
  latLng,
  city,
  state,
  url,
  showHeader = false,
  delay = 0
) => {
  const position = new google.maps.LatLng(latLng.lat(), latLng.lng());
  const icon = { url, anchor: new google.maps.Point(13, 18) };

  return window.setTimeout(() => {
    const marker = new google.maps.Marker({
      map,
      draggable: false,
      animation: google.maps.Animation.DROP,
      position,
      icon,
      zIndex: 2147483647,
    });

    if (showHeader) {
      return addInfoBox(map, marker, city, state);
    }
  }, delay);
};

const addBezierPath = (map, fromLatLng, toLatLng, delay) => {
  const points = [
    [fromLatLng.lat(), fromLatLng.lng()],
    [toLatLng.lat(), toLatLng.lng()],
  ];

  return window.setTimeout(() => mapBezier({ map, points }), delay);
};

export function dynamicMap(
  element,
  fromAddress,
  toAddress,
  showHeader,
  isPublic = false
) {
  if (element == null || (fromAddress == null && toAddress == null)) {
    return;
  }

  return Promise.all(
    [fromAddress, toAddress].map(address => {
      return geocode(
        isPublic ? null : address.street,
        address.city,
        address.state,
        address.zip
      );
    })
  ).then(([geocodedFrom, geocodedTo]) => {
    return new Promise(resolve => {
      const initBounds = new google.maps.LatLngBounds();
      initBounds.extend(geocodedFrom);
      initBounds.extend(geocodedTo);

      const map = new google.maps.Map(element, {
        mapTypeControl: false,
        maxZoom: 13,
        minZoom: 3,
      });
      // Resolve when the map is done drawing
      google.maps.event.addListenerOnce(map, 'idle', () => resolve());

      map.fitBounds(initBounds);

      map.mapTypes.set('updater', getDynamicMapStyle());
      map.setMapTypeId('updater');

      addMarker(
        map,
        geocodedFrom,
        fromAddress.city,
        fromAddress.state,
        fromDot,
        showHeader,
        350
      );
      addMarker(
        map,
        geocodedTo,
        toAddress.city,
        toAddress.state,
        toDot,
        showHeader,
        1700
      );
      addBezierPath(map, geocodedFrom, geocodedTo, 2500);
    });
  });
}
